import { JurisdictionFact } from '../../../tax-settings/JurisdictionFact';
import { INFORMATIONAL_NOTICE } from '../../../tax-settings/checklistContent';
import { TaxSetupChecklist, type RequirementActions } from '../../../tax-settings/TaxSetupChecklist';
import { TaxSetupStatusPanel } from '../../../tax-settings/TaxSetupStatusPanel';
import { useJurisdictionCatalog } from '../../../tax-settings/useJurisdictionCatalog';
import type { AdminStateTaxResponse, AdminTaxState } from '../../types';
import type { AdminStateTaxState } from './useAdminStateTax';
import type { AdminTaxSetupState } from './useAdminTaxSetup';

const LOCAL_HELP =
  'EPN cannot yet determine local tax jurisdictions (a city, county or school district) from the addresses. It never assumes there are none, so a reviewer has to check.';
const LOCAL_SOURCE = 'Not determined yet. EPN has no local jurisdiction determination, so this is never shown as "none".';
const OVERRIDE_NOTE =
  'These facts are read from the employee\'s work location and home address, so they are not edited here. To correct one, fix the work location or the home address in Employee Setup. Manual overrides are not available yet: if a determination looks wrong, contact EPN support.';

/** Where an administrator supplies the two facts the state is read from. The facts are never edited in Tax Settings, so each missing one links to the place that owns it (`?section=` deep-links a sub-section, EPN-94). */
function missingFactActions(employeeId: string): RequirementActions {
  const setup = `/people/${employeeId}/employee-setup`;
  return {
    workState: { label: 'Open Work Location in Employee Setup', to: `${setup}?section=location` },
    residenceState: { label: 'Open Home/Residence Address in Employee Setup', to: `${setup}?section=address` },
  };
}

const money = (value: number) => `$${value.toFixed(2)}`;

/** The source line under a state fact. It says "determined" only for the sources that really determine it; otherwise it is the reason the state is unknown. */
function sourceLine(state: AdminTaxState): string {
  switch (state.source) {
    case 'PrimaryWorkLocation':
      return 'Determined from the primary work location.';
    case 'HomeAddressRemote':
      return 'Determined from the home address, because the employee works from home.';
    case 'HomeAddress':
      return 'From the home address.';
    default:
      return state.note;
  }
}

function additionalWithholding(data: AdminStateTaxResponse): string {
  const draft = data.submittedByEmployee.draft;
  if (!draft || (draft.additionalWithholdingValue === null && draft.additionalWithholdingState === null)) return 'None requested.';
  const amount = draft.additionalWithholdingValue === null ? 'An amount was not saved' : `${money(draft.additionalWithholdingValue)} per pay period`;
  return `${amount}${draft.additionalWithholdingState ? ` for ${draft.additionalWithholdingState}` : ''}. Requested by the employee as a draft. It is not used for payroll yet.`;
}

/**
 * Tax Settings (administrator). Read-only and informational: the tax setup status with its counts, the work and residence facts with where each comes
 * from, the seven-row guided checklist (including Unemployment Insurance and Reciprocity) and the employee's additional withholding request.
 * Nothing here is edited or saved, nothing is calculated, and no status means payroll is ready. The reciprocity review follows in its own section.
 */
export function AdminTaxSettingsPanel({ employeeId, stateTax, taxSetup }: { employeeId: string; stateTax: AdminStateTaxState; taxSetup: AdminTaxSetupState }) {
  const catalog = useJurisdictionCatalog();
  const data = stateTax.data;
  const reasonUnavailable = stateTax.failed ? 'The state income tax details could not be loaded. Open State Income Tax to retry.' : 'Loading.';

  const stateFact = (label: string, testId: string, pick: 'workState' | 'residenceState') => {
    const state = data?.[pick];
    const name = state?.name ?? state?.code ?? null;
    return (
      <JurisdictionFact
        label={label}
        text={name}
        entry={catalog.find(state?.code)}
        source={state ? sourceLine(state) : reasonUnavailable}
        help={state ? state.note : reasonUnavailable}
        testId={testId}
      />
    );
  };

  const workLocation = data?.workLocation;
  return (
    <section className="profile-section">
      <h3 className="profile-section-title">
        Tax Settings <span className="profile-section-badge">Informational</span>
      </h3>
      <p className="muted">{INFORMATIONAL_NOTICE}</p>

      <TaxSetupStatusPanel status={taxSetup.data} failed={taxSetup.failed} onRetry={taxSetup.reload} headingLevel={4} />

      <h4 className="ts-subhead">Where this employee works and lives</h4>
      <div className="ts-facts">
        {stateFact('Tax Work State', 'fact-work-state', 'workState')}
        {stateFact('Tax Residence State', 'fact-residence-state', 'residenceState')}
        <JurisdictionFact
          label="Work Location"
          text={workLocation ? (workLocation.primaryWorkLocationId ? 'Primary work location assigned' : null) : null}
          emptyText={workLocation ? 'No primary work location assigned' : 'Not available yet'}
          source={workLocation ? (workLocation.lookupUnavailable ? 'The location\'s tax details could not be looked up right now.' : 'Assigned in Employee Setup, Work Details.') : reasonUnavailable}
          testId="fact-work-location"
        />
        <JurisdictionFact label="Local Tax Jurisdiction — Work" text={null} emptyText="Not determined yet" chip="NeedsReview" source={LOCAL_SOURCE} help={LOCAL_HELP} testId="fact-work-local" />
        <JurisdictionFact label="Local Tax Jurisdiction — Residence" text={null} emptyText="Not determined yet" chip="NeedsReview" source={LOCAL_SOURCE} help={LOCAL_HELP} testId="fact-residence-local" />
      </div>
      <p className="muted ts-override-note" data-testid="override-note">
        {OVERRIDE_NOTE}
      </p>

      <h4 className="ts-subhead">Tax setup checklist</h4>
      {taxSetup.data ? (
        <TaxSetupChecklist status={taxSetup.data} view="admin" actions={missingFactActions(employeeId)} />
      ) : (
        <p className="muted" role="status">
          {taxSetup.failed ? 'The checklist could not be loaded.' : 'Loading the checklist…'}
        </p>
      )}

      <h4 className="ts-subhead">Additional State Tax Withholding</h4>
      <p className="muted" data-testid="additional-withholding">
        {data ? additionalWithholding(data) : reasonUnavailable} The employee asks for this in their own setup. You cannot change it here, and it can only add to
        required withholding.
      </p>
    </section>
  );
}
